import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { ROUTES } from '../../constants/routes';
import { cookieConsent } from '../../constants/messages';
import {
  COOKIE_CONSENT_STORAGE_KEY,
  CookieConsentChoice,
  privacyService,
} from '../../services/privacyService';

/**
 * Banner de consentimiento de cookies (Ley 21.719).
 * Tarjeta flotante en la esquina inferior; no tapa los formularios ni los modales legales (z-index menor).
 */
const CookieConsentBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [policyVersion, setPolicyVersion] = useState('');

  useEffect(() => {
    setMounted(true);
    try {
      const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
      if (!raw) {
        setVisible(true);
      }
    } catch {
      setVisible(true);
    }
    privacyService
      .getComplianceInfo()
      .then((info) => setPolicyVersion(info.privacy_policy_version))
      .catch(() => {});
  }, []);

  const persist = async (analytics: boolean) => {
    let version = policyVersion;
    if (!version) {
      try {
        const info = await privacyService.getComplianceInfo();
        version = info.privacy_policy_version;
        setPolicyVersion(version);
      } catch {
        version = '';
      }
    }
    const choice: CookieConsentChoice = {
      essential: true,
      analytics,
      version,
      savedAt: new Date().toISOString(),
    };
    try {
      localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(choice));
    } catch {
      // localStorage puede fallar en modo privado estricto; no bloqueamos la UX
    }
    if (version) {
      try {
        await privacyService.recordCookieConsent(analytics, version);
      } catch {
        // El registro remoto es best-effort; la preferencia local ya quedó guardada
      }
    }
    setVisible(false);
  };

  if (!mounted || !visible) return null;

  return createPortal(
    <div
      className="fixed inset-x-3 bottom-3 z-[90] rounded-[24px] bg-white p-5 font-lp-sans text-lp-ink shadow-[0_20px_50px_-15px_rgba(30,43,40,0.35)] ring-1 ring-lp-ink/10 sm:inset-x-auto sm:bottom-5 sm:left-5 sm:max-w-md sm:p-6"
      role="dialog"
      aria-label={cookieConsent.ariaLabel}
      aria-describedby="cookie-consent-description"
    >
      <div className="flex gap-4">
        <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full bg-lp-peach sm:flex" aria-hidden="true">
          <Cookie className="h-5 w-5 text-lp-clay" />
        </span>
        <div className="min-w-0" id="cookie-consent-description">
          <p className="font-lp-serif text-lg leading-snug">{cookieConsent.title}</p>
          <p className="mt-1.5 line-clamp-4 text-sm leading-relaxed text-lp-ink/65 sm:line-clamp-none">
            {cookieConsent.description}
          </p>
          <Link to={ROUTES.PRIVACY} className="mt-2 inline-block text-sm font-medium text-lp-teal hover:underline">
            Ver política de privacidad
          </Link>
        </div>
      </div>
      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row">
        <button
          type="button"
          onClick={() => persist(false)}
          className="flex-1 whitespace-nowrap rounded-full border border-lp-ink/15 px-5 py-2.5 text-sm font-medium transition hover:border-lp-teal hover:text-lp-teal"
          aria-label={cookieConsent.essentialOnlyAria}
        >
          {cookieConsent.essentialOnly}
        </button>
        <button
          type="button"
          onClick={() => persist(true)}
          className="flex-1 whitespace-nowrap rounded-full bg-lp-ink px-5 py-2.5 text-sm font-medium text-lp-paper transition hover:bg-lp-teal"
          aria-label={cookieConsent.acceptAllAria}
        >
          {cookieConsent.acceptAll}
        </button>
      </div>
      {policyVersion ? (
        <p className="mt-3 text-[11px] leading-tight text-lp-ink/40">
          {cookieConsent.implicitConsent} {cookieConsent.legalNote} {policyVersion}.
        </p>
      ) : null}
    </div>,
    document.body
  );
};

export default CookieConsentBanner;
